import { useQuery } from '@tanstack/react-query';
import {
  listCipEnrollments,
  listEtcEnrollments,
  listHmfEnrollments,
  listHmfpEnrollments,
} from './enrollments';
import type {
  ListEnrollmentsParams,
  ListEnrollmentsResponse,
} from './enrollments';
import {
  EnrollmentSchema,
  EnrollmentType,
  Enrollment,
  TICKET_TYPE,
  PenaltyEnrollmentSchema,
} from '../model';

export const ENROLLMENT_QUERY_NAMESPACE = 'enrollment' as const;

const EMPTY_RESPONSE: ListEnrollmentsResponse = {
  data: [],
  extra: { totalElement: 0, totalPage: 0 },
};

const transformEnrollments = (data: Enrollment[], type: EnrollmentType) => {
  const validData = data
    .map((e: Enrollment) => {
      try {
        return EnrollmentSchema.parse(e, { reportInput: true });
      } catch (err) {
        console.error(err);
        return undefined;
      }
    })
    .filter((e) => !!e)
    .map((d) => ({
      id: d.id,
      email: d.account.email,
      plateNumber: d.plateNumber,
      idNumber: d.idNumber,
      taxIdNumber: d.taxIdNumber,
      payInterval: d.payInterval,
      createDateTime: d.createDateTime,
      updateDateTime: d.updateDateTime,
      state: d.state,
      isSuspended: d.isSuspended,
      type,
    }));

  return validData;
};

const getEnrollments = async (params?: ListEnrollmentsParams) => {
  const listParams = { page: 1, pageSize: 99999, ...params };

  const etc = await listEtcEnrollments(listParams).catch(() => EMPTY_RESPONSE);
  const hmf = await listHmfEnrollments(listParams).catch(() => EMPTY_RESPONSE);
  const hmfp = await listHmfpEnrollments(listParams).catch(
    () => EMPTY_RESPONSE,
  );
  const cip = await listCipEnrollments(listParams).catch(() => EMPTY_RESPONSE);

  const etcData = transformEnrollments(etc.data, TICKET_TYPE.ETC_TICKETS);
  const cipData = cip.data
    .map((e: Enrollment) => {
      try {
        return PenaltyEnrollmentSchema.parse(e, { reportInput: true });
      } catch (err) {
        console.error(err);
        return undefined;
      }
    })
    .filter((e) => !!e)
    .map((d) => ({
      id: d.id,
      email: d.account.email,
      plateNumber: d.plateNumber,
      idNumber: d.idNumber,
      taxIdNumber: d.taxIdNumber,
      payInterval: d.payInterval,
      createDateTime: d.createDateTime,
      updateDateTime: d.updateDateTime,
      state: d.state,
      isSuspended: d.isSuspended,
      type: TICKET_TYPE.COMPULSORY_INSURANCE_PENALTY_TICKETS,
      vehicleType: d.vehicleType,
    }));

  const hmfpData = transformEnrollments(
    hmfp.data,
    TICKET_TYPE.HIGHWAY_MAINTENANCE_FEE_PENALTY_TICKETS,
  );
  const hmfData = transformEnrollments(
    hmf.data,
    TICKET_TYPE.HIGHWAY_MAINTENANCE_FEE_TICKETS,
  );

  const list = [...etcData, ...cipData, ...hmfpData, ...hmfData].sort(
    (a, b) =>
      Number(new Date(b.createDateTime)) - Number(new Date(a.createDateTime)),
  );

  return list;
};

export function useEnrollments(params?: ListEnrollmentsParams) {
  return useQuery({
    queryKey: [ENROLLMENT_QUERY_NAMESPACE, 'list', params],
    queryFn: () => {
      return getEnrollments(params);
    },
  });
}
